// Libraries
import { useQuery, useInfiniteQuery, useMutation } from '@tanstack/react-query';

export const BaseStaleTime = 10 * 1000; // 10 seconds

// Query Client

/**
 * @typedef {import('@tanstack/react-query').QueryClient} QueryClient
 */

// Options

/**
 * @typedef {import('@tanstack/react-query').UseQueryOptions} UseQueryOptions
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').UndefinedInitialDataOptions<TData, TError>} UndefinedInitialDataOptions
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').DefinedInitialDataOptions<TData, TError>} DefinedInitialDataOptions
 */

/**
 * @typedef {import('@tanstack/react-query').UseInfiniteQueryOptions} UseInfiniteQueryOptions
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').UndefinedInitialDataInfiniteOptions<TData, TError>} UndefinedInitialDataInfiniteOptions
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').DefinedInitialDataInfiniteOptions<TData, TError>} DefinedInitialDataInfiniteOptions
 */

/**
 * @template TData
 * @template TVariables
 * @template [TError=Error]
 * @template [TContext=any]
 * @typedef {import('@tanstack/react-query').UseMutationOptions<TData, TError, TVariables, TContext>} UseMutationOptions
 */

// Results

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').UseQueryResult<TData, TError>} UseQueryResult
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').DefinedUseQueryResult<TData, TError>} DefinedUseQueryResult
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').UseInfiniteQueryResult<TData, TError>} UseInfiniteQueryResult
 */

/**
 * @template TData
 * @template [TError=Error]
 * @typedef {import('@tanstack/react-query').DefinedUseInfiniteQueryResult<TData, TError>} DefinedUseInfiniteQueryResult
 */

/**
 * @template TData
 * @template TVariables
 * @template [TError=Error]
 * @template [TContext=any]
 * @typedef {import('@tanstack/react-query').UseMutationResult<TData, TError, TVariables, TContext>} UseMutationResult
 */

/**
 * Allows a specific return type to be mapped to useQuery.
 *
 * @template TData
 * @param {UndefinedInitialDataOptions<TData> | DefinedInitialDataOptions<TData> | UseQueryOptions} options
 * @param {QueryClient | undefined} queryClient
 * @returns {UseQueryResult<TData> | DefinedUseQueryResult<TData>}
 */
export const useSpecificQuery = (options, queryClient = undefined) =>
    useQuery(
        {
            staleTime: BaseStaleTime,
            refetchOnMount: true, // true is the default for this, but making it visible here is helpful
            enabled: true, // Same as above
            ...(options || [])
        },
        queryClient
    );

/**
 * Allows a specific return type to be mapped to useInfiniteQuery.
 *
 * @template TData
 * @param {UndefinedInitialDataInfiniteOptions<TData> | DefinedInitialDataInfiniteOptions<TData> | UseInfiniteQueryOptions} options
 * @param {QueryClient | undefined} queryClient
 * @returns {UseInfiniteQueryResult<TData> | DefinedUseInfiniteQueryResult<TData>}
 */
export const useSpecificInfiniteQuery = (options, queryClient = undefined) =>
    useInfiniteQuery(
        {
            staleTime: BaseStaleTime,
            refetchOnMount: true, // true is the default for this, but making it visible here is helpful
            enabled: true, // Same as above
            ...(options || [])
        },
        queryClient
    );

/**
 * Allows a specific return type to be mapped to useMutation.
 *
 * @template TData
 * @template TVariables
 * @param {UseMutationOptions<TData, TVariables>} options
 * @param {QueryClient | undefined} queryClient
 * @returns {UseMutationResult<TData, TVariables>}
 */
export const useSpecificMutation = (options, queryClient = undefined) => useMutation(options, queryClient);
